import { call, put, takeLatest } from 'redux-saga/effects';
import { IMAGING_FILTER_DATA_REQUEST, IMAGING_CAT_DETAILS_DATA_REQUEST, IMAGING_NET_CAT_DETAILS_DATA_REQUEST, IMAGING_EXPORT_LINK_REQUEST, IMAGING_CATEGORY_VIEW_DATA_REQUEST } from '../../actions/actionTypes';
import {
  imagingDetailsDataSuccess, imagingDetailsDataError, imagingDetailsDataLoading, imagingFilterDataLoading, imagingFilterDataError, imagingFilterDataSuccess, imagingNetDetailsDataLoading, imagingNetDetailsDataError,
  imagingNetDetailsDataSuccess,
  imagingDetailsExportLoading,
  imagingDetailsExportError,
  imagingDetailsExportSuccess,
  imagingCategoryViewLoading,
  imagingCategoryViewError,
  imagingCategoryViewSuccess,
} from '../../actions/Ancillary/Imaging/ImagingAction';
import {
   getImagingcategoryData, getTopFilterData, getNetImagingcategoryData, getExporDetails, getExporDetailsStatus, getExporDetailsLinks,
  getImagingCatViewData, getDateFilterData
} from '@src/middleware/services/Ancillary/Imaging/imaging.service';


export function* fetchTopFilterDetails(action: any) {
  try {
    yield put(imagingFilterDataLoading(true));

    const response = yield call(getDateFilterData);
    let topFilterPayload = action?.payload;
    if (!topFilterPayload?.NetworkLeverTopFilter?.year && response?.Data) {
      const defaultPeriod = response.Data.find((item: any) => item.Default === true);
      if (defaultPeriod) {
        topFilterPayload = {
          ...topFilterPayload,
          NetworkLeverTopFilter: {
            ...topFilterPayload?.NetworkLeverTopFilter,
            year: new Date(defaultPeriod.StartDate).getFullYear()
          }
        };
      }
    }

    const topFilter = yield call(getTopFilterData, topFilterPayload);

    yield put(imagingFilterDataLoading(false));

    if ((!topFilter || topFilter?.Status !== "OK")) {
      // Handle error
      yield put(imagingFilterDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const filterData = {
        dateFilter: response?.Data,
        topFilter: topFilter?.Data
      };
      yield put(imagingFilterDataSuccess(filterData));
    }
  } catch (error) {
    yield put(imagingFilterDataLoading(false));
    yield put(imagingFilterDataError("An error occurred"));
  }
};

export function* fetchImagingCategoryDetails(action: any) {
  try {
    yield put(imagingDetailsDataLoading(true));

    const preferredResponse = yield call(getImagingcategoryData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getImagingcategoryData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(imagingDetailsDataLoading(false));

    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(imagingDetailsDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };

      yield put(imagingDetailsDataSuccess(Data));
    }
  } catch (error) {
    yield put(imagingDetailsDataLoading(false));
    yield put(imagingDetailsDataError("An error occurred"));
  }
};

export function* fetchImagingNetCategoryDetails(action: any) {
  try {
    yield put(imagingNetDetailsDataLoading(true));

    // Call the API services
    const preferredResponse = yield call(getNetImagingcategoryData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getNetImagingcategoryData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(imagingNetDetailsDataLoading(false));
    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(imagingNetDetailsDataError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };

      yield put(imagingNetDetailsDataSuccess(Data));
    }
  } catch (error) {
    yield put(imagingNetDetailsDataLoading(false));
    yield put(imagingNetDetailsDataError("An error occurred"));
  }
};

export function* fetchImagingDetailsExportLinks(action: any) {
  try {
    yield put(imagingDetailsExportLoading(true));

    // Step 1: Call getExporDetails API
    // Filter out empty values and add required fields
    const filteredPayload = Object.entries({ ...action?.payload }).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const exportPayload = { ...filteredPayload, OutputFormat: "ND_JSON"};
    const exportDetailsResponse = yield call(getExporDetails, exportPayload);

    if (!exportDetailsResponse || exportDetailsResponse?.Status !== "OK") {
      yield put(imagingDetailsExportLoading(false));
      yield put(imagingDetailsExportError("Failed to initiate export"));
      return;
    }

    const exportId = exportDetailsResponse?.Data?.Id;

    if (!exportId) {
      yield put(imagingDetailsExportLoading(false));
      yield put(imagingDetailsExportError("Export ID not received"));
      return;
    }

    // Step 2: Poll getExporDetailsStatus API with the received ID
    const maxRetries = 100;
    let currentRetry = 0;
    let isCompleted = false;
    let statusResponse;

    while (currentRetry < maxRetries && !isCompleted) {
      const statusPayload = { Id: exportId };

      statusResponse = yield call(getExporDetailsStatus, statusPayload);

      if (!statusResponse || statusResponse?.Status !== "OK") {
        yield put(imagingDetailsExportLoading(false));
        yield put(imagingDetailsExportError("Failed to fetch export status"));
        return;
      }

      // Check if Data is null - stop polling
      if (statusResponse?.Data === null || statusResponse?.Data === undefined) {
        yield put(imagingDetailsExportLoading(false));
        yield put(imagingDetailsExportError("Export data not available"));
        return;
      }

      // Check if status is completed
      const exportStatus = statusResponse?.Data?.Status;

      if (exportStatus === "Completed") {
        isCompleted = true;
      } else {
        currentRetry++;
        if (currentRetry < maxRetries) {
          // Wait for 3 seconds before next retry
          yield new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
    }

    // Check if export completed or max retries reached
    if (!isCompleted) {
      yield put(imagingDetailsExportLoading(false));
      yield put(imagingDetailsExportError("Export process timed out. Please try again later."));
      return;
    }

    // Step 3: Call getExporDetailsLinks API
    const linksPayload = { Id: exportId };

    const linksResponse = yield call(getExporDetailsLinks, linksPayload);

    // Reset loading state
    yield put(imagingDetailsExportLoading(false));
    if (!linksResponse || linksResponse?.Status !== "OK") {
      yield put(imagingDetailsExportError("Failed to fetch export links"));
    } else {
      yield put(imagingDetailsExportSuccess(linksResponse?.Data));
    }

  } catch (error) {
    yield put(imagingDetailsExportLoading(false));
    yield put(imagingDetailsExportError("An error occurred during export"));
  }
};

export function* fetchImagingCategoryViewData(action: any) {
  try {
    yield put(imagingCategoryViewLoading(true));

    // Call the API services
    const preferredResponse = yield call(getImagingCatViewData, { ...action?.payload, TypeOfService: "Preferred" });
    const nonPreferredResponse = yield call(getImagingCatViewData, { ...action?.payload, TypeOfService: "Non-Preferred" });

    // Reset loading state
    yield put(imagingCategoryViewLoading(false));

    if ((!preferredResponse || preferredResponse?.Status !== "OK") || (!nonPreferredResponse || nonPreferredResponse?.Status !== "OK")) {
      // Handle error
      yield put(imagingCategoryViewError("Failed to fetch data"));
    } else {
      // Handle success - combine both responses
      const Data = {
        preferred: preferredResponse?.Data,
        nonPreferred: nonPreferredResponse?.Data
      };

      yield put(imagingCategoryViewSuccess(Data));
    }
  } catch (error) {
    yield put(imagingCategoryViewLoading(false));
    yield put(imagingCategoryViewError("An error occurred"));
  }
}


export function* ImagingSaga() {
  yield takeLatest(IMAGING_FILTER_DATA_REQUEST, fetchTopFilterDetails);
  yield takeLatest(IMAGING_CAT_DETAILS_DATA_REQUEST, fetchImagingCategoryDetails);
  yield takeLatest(IMAGING_NET_CAT_DETAILS_DATA_REQUEST, fetchImagingNetCategoryDetails);
  yield takeLatest(IMAGING_EXPORT_LINK_REQUEST, fetchImagingDetailsExportLinks);
  yield takeLatest(IMAGING_CATEGORY_VIEW_DATA_REQUEST, fetchImagingCategoryViewData);
}
export default [ImagingSaga];